import { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../config';
import { DpoBeamBackground, EMBEDDED_CSS as DPO_BEAM_CSS } from './distributorPoShared';
import { formatDate } from '../utils/formatDate';
const BASE = API_BASE;

const fmtNum = (n) => (n || 0).toLocaleString('en-IN');
const fmtINR = (n) => '₹' + (n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const todayISO = () => new Date().toISOString().slice(0, 10);

// Groups institution_master rows by institution_name+location — several rows
// can share both but carry a different address (e.g. a re-registered
// franchise), and the picker needs one suggestion per group, not per row.
function groupInstitutions(institutions) {
    const map = new Map();
    for (const inst of institutions) {
        const key = `${inst.institution_name}||${inst.location || ''}`;
        if (!map.has(key)) map.set(key, { key, institution_name: inst.institution_name, location: inst.location, rows: [] });
        map.get(key).rows.push(inst);
    }
    return Array.from(map.values());
}

// Fetch -> blob -> throwaway <a> click, same pattern Invoices.jsx uses for
// its packing-slip download — a plain file response, not a data: URI, so it
// works reliably on mobile browsers (unlike embedding a PDF inline).
async function downloadPdf(poNumber, showToast) {
    try {
        const res = await fetch(`${BASE}/institutional-orders?action=pdf&po_number=${encodeURIComponent(poNumber)}`);
        const contentType = res.headers.get('content-type') || '';
        if (!res.ok || contentType.includes('json')) {
            const body = await res.json().catch(() => ({}));
            showToast(body.error || 'Could not generate PDF');
            return;
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${poNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    } catch (e) {
        console.error(e);
        showToast('Could not download PDF');
    }
}

const TABS = [
    { key: 'create', label: 'Create Order', sub: 'Raise a new institutional order', icon: '📝' },
    { key: 'history', label: 'History', sub: 'View, classify or delete past orders', icon: '🕒' },
    { key: 'manage', label: 'Manage Institutions', sub: 'Add or edit institutions & contacts', icon: '🏢' },
];

// Institutional Orders — one sidebar entry, three internal tabs, mobile-first
// throughout (sales staff place these orders from a phone in the field, not
// always a laptop). See plans/institutional-orders-plan.md for the full
// design rationale — this deliberately does NOT reuse DistributorPO.jsx's
// desktop/region-margin layout; it's a separate, simpler flow.
export default function InstitutionalOrders({ user }) {
    const [tab, setTab] = useState(null);
    const [institutions, setInstitutions] = useState([]);
    const [loadingInstitutions, setLoadingInstitutions] = useState(false);
    const [toast, setToast] = useState(null);

    const loadInstitutions = useCallback(async () => {
        setLoadingInstitutions(true);
        try {
            const res = await fetch(`${BASE}/institution-master?action=list`);
            const data = await res.json();
            setInstitutions(Array.isArray(data) ? data : []);
        } catch (e) { console.error(e); }
        finally { setLoadingInstitutions(false); }
    }, []);

    useEffect(() => { loadInstitutions(); }, [loadInstitutions]);

    function showToast(msg) {
        setToast(msg);
        setTimeout(() => setToast(null), 3500);
    }

    const activeTabMeta = TABS.find(t => t.key === tab);
    const displayName = user?.display_name || user?.username || '';

    return (
        <div className="io-page" data-tutorial="institutional-orders-table" style={S.page}>
            <style>{EMBEDDED_CSS}</style>

            {!tab && displayName && (
                <h2 style={S.greeting}>Hi, {displayName}! Create Your Retail Order Here</h2>
            )}

            <div style={S.headerBar}>
                <DpoBeamBackground />
                {tab && (
                    <button type="button" style={{ ...S.backBtn, position: 'relative', zIndex: 1 }} onClick={() => setTab(null)}>← Back</button>
                )}
                <h1 style={{ ...S.h1, position: 'relative', zIndex: 1 }}>{activeTabMeta ? activeTabMeta.label : 'Institutional Orders'}</h1>
            </div>

            {!tab && (
                <div style={S.startGrid}>
                    {TABS.map(t => (
                        <button key={t.key} className="io-startCard" style={S.startCard} onClick={() => setTab(t.key)}>
                            <span style={S.startIcon}>{t.icon}</span>
                            <span style={{ minWidth: 0 }}>
                                <span style={S.startTitle}>{t.label}</span>
                                <span style={S.startSub}>{t.sub}</span>
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {tab === 'create' && (
                <CreateOrderTab
                    user={user}
                    institutions={institutions}
                    loadingInstitutions={loadingInstitutions}
                    reloadInstitutions={loadInstitutions}
                    showToast={showToast}
                />
            )}
            {tab === 'history' && <HistoryTab showToast={showToast} />}
            {tab === 'manage' && (
                <ManageInstitutionsTab institutions={institutions} loading={loadingInstitutions} reload={loadInstitutions} showToast={showToast} />
            )}

            {toast && <div style={S.toast}>{toast}</div>}
        </div>
    );
}

/* ============================== Create Order ============================== */

function CreateOrderTab({ user, institutions, loadingInstitutions, reloadInstitutions, showToast }) {
    const [instSearch, setInstSearch] = useState('');
    const [instOpen, setInstOpen] = useState(false);
    const [selectedInst, setSelectedInst] = useState(null);
    const [addressStep, setAddressStep] = useState(null); // array of same-name+location rows awaiting an address pick
    const [contactId, setContactId] = useState('');
    const [newContactName, setNewContactName] = useState('');
    const [newContactMobile, setNewContactMobile] = useState('');
    const [showNewContact, setShowNewContact] = useState(false);

    const [poDate, setPoDate] = useState(todayISO());
    const [location, setLocation] = useState('');
    const [autoPoNumber, setAutoPoNumber] = useState(true);
    const [poNumber, setPoNumber] = useState('');
    const [remark, setRemark] = useState('');

    const [skuSearch, setSkuSearch] = useState('');
    const [skuResults, setSkuResults] = useState([]);
    const [skuOpen, setSkuOpen] = useState(false);
    const [lines, setLines] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [showReview, setShowReview] = useState(false);

    // Same institution_name+location can have more than one address on file
    // (e.g. a franchise chain re-registered with a different Buyer Address) —
    // group them so the picker offers one suggestion per name+location, then
    // asks which address only when a group actually has more than one.
    const institutionGroups = groupInstitutions(institutions);

    const filteredGroups = institutionGroups.filter(g => {
        const q = instSearch.trim().toLowerCase();
        if (!q) return true;
        return g.institution_name.toLowerCase().includes(q) || (g.location || '').toLowerCase().includes(q);
    });

    function pickInstitution(inst) {
        setSelectedInst(inst);
        setLocation(inst.location || '');
        setInstSearch(inst.institution_name);
        setInstOpen(false);
        setAddressStep(null);
        setContactId('');
        setShowNewContact(false);
    }

    function pickGroup(group) {
        if (group.rows.length === 1) { pickInstitution(group.rows[0]); return; }
        setAddressStep(group.rows);
    }

    useEffect(() => {
        if (!skuSearch.trim()) { setSkuResults([]); return; }
        const t = setTimeout(async () => {
            try {
                const res = await fetch(`${BASE}/institutional-orders?action=skus&search=${encodeURIComponent(skuSearch)}`);
                const data = await res.json();
                setSkuResults(Array.isArray(data) ? data : []);
            } catch (e) { console.error(e); }
        }, 250);
        return () => clearTimeout(t);
    }, [skuSearch]);

    // Live preview of the next auto-generated RTL number — fires on mount
    // and again whenever the "Auto generate PO no." checkbox is re-checked.
    useEffect(() => {
        if (!autoPoNumber) return;
        fetch(`${BASE}/institutional-orders?action=next-po-number`)
            .then(r => r.json())
            .then(d => setPoNumber(d.po_number || ''))
            .catch(() => {});
    }, [autoPoNumber]);

    function addLine(sku) {
        if (lines.some(l => l.ean === sku.ean_number)) { showToast('Already added'); return; }
        setLines(ls => [...ls, { ean: sku.ean_number, product_name: sku.product_name, mrp: sku.mrp, gst_percent: sku.gst_percent, hsn_number: sku.hsn_number, qty: 1 }]);
        setSkuSearch('');
        setSkuResults([]);
        setSkuOpen(false);
    }
    function updateQty(ean, qty) {
        setLines(ls => ls.map(l => l.ean === ean ? { ...l, qty } : l));
    }
    function updateMrp(ean, mrp) {
        setLines(ls => ls.map(l => l.ean === ean ? { ...l, mrp } : l));
    }
    function updateGst(ean, gst_percent) {
        setLines(ls => ls.map(l => l.ean === ean ? { ...l, gst_percent } : l));
    }
    function removeLine(ean) {
        setLines(ls => ls.filter(l => l.ean !== ean));
    }

    const totalUnits = lines.reduce((s, l) => s + Number(l.qty || 0), 0);
    const totalBase = lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.mrp || 0), 0);
    const totalGst = lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.mrp || 0) * Number(l.gst_percent || 0) / 100, 0);
    const totalAmount = totalBase + totalGst;

    function validate() {
        if (!selectedInst) return 'Select an institution first';
        if (!contactId && !(showNewContact && newContactName.trim())) return 'Select or add a contact person';
        if (lines.length === 0) return 'Add at least one product line';
        if (lines.some(l => !(Number(l.qty) > 0))) return 'Every line needs a quantity greater than 0';
        if (!autoPoNumber && !poNumber.trim()) return 'Enter a PO number or enable auto-generate';
        return null;
    }

    function openReview() {
        const err = validate();
        if (err) { showToast(err); return; }
        setShowReview(true);
    }

    const selectedContact = (selectedInst?.contacts || []).find(c => String(c.id) === String(contactId));

    async function handleSubmit() {
        const err = validate();
        if (err) { showToast(err); setShowReview(false); return; }

        setSubmitting(true);
        try {
            let finalContactId = contactId;
            if (!finalContactId && showNewContact && newContactName.trim()) {
                const cRes = await fetch(`${BASE}/institution-master?action=create-contact`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ institution_id: selectedInst.id, contact_person: newContactName.trim(), mobile: newContactMobile.trim() || null }),
                });
                const cData = await cRes.json();
                if (!cRes.ok) { showToast(cData.error || 'Could not add contact'); setSubmitting(false); return; }
                finalContactId = cData.id;
                reloadInstitutions();
            }

            const res = await fetch(`${BASE}/institutional-orders?action=create`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    institution_name: selectedInst.institution_name,
                    institution_master_contacts_id: finalContactId,
                    location,
                    po_date: poDate,
                    created_by: user?.username || user?.name || 'Sales Portal',
                    lines,
                    po_number: autoPoNumber ? undefined : poNumber.trim(),
                    remark: remark.trim(),
                }),
            });
            const data = await res.json();
            if (!res.ok) { showToast(data.error || 'Could not submit order'); setSubmitting(false); return; }

            showToast(`Order ${data.po_number} submitted`);
            setShowReview(false);
            setSelectedInst(null); setInstSearch(''); setContactId('');
            setShowNewContact(false); setNewContactName(''); setNewContactMobile('');
            setLocation(''); setPoDate(todayISO()); setLines([]); setRemark('');
            setAutoPoNumber(true);
            fetch(`${BASE}/institutional-orders?action=next-po-number`).then(r => r.json()).then(d => setPoNumber(d.po_number || '')).catch(() => {});
        } catch (e) {
            console.error(e);
            showToast('Something went wrong — try again');
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div style={S.formWrap}>
            <div style={S.section}>
                <div style={S.fieldLabel}>Institution</div>
                <div style={{ position: 'relative' }}>
                    <input
                        style={S.input}
                        placeholder={loadingInstitutions ? 'Loading institutions...' : 'Search institution by name or location'}
                        value={instSearch}
                        onFocus={() => setInstOpen(true)}
                        onChange={e => { setInstSearch(e.target.value); setSelectedInst(null); setAddressStep(null); setInstOpen(true); }}
                    />
                    {instOpen && instSearch.trim() && !addressStep && (
                        <div style={S.suggestBox}>
                            {filteredGroups.length === 0 ? (
                                <div style={S.suggestEmpty}>No match — check spelling, or add it in "Manage Institutions".</div>
                            ) : filteredGroups.slice(0, 20).map(g => (
                                <div key={g.key} style={S.suggestItem} onClick={() => pickGroup(g)}>
                                    <div style={{ fontWeight: 700 }}>{g.institution_name}</div>
                                    <div style={{ fontSize: 12, color: '#8C82A0' }}>
                                        {g.location || 'No location set'}
                                        {g.rows.length > 1 ? ` · ${g.rows.length} addresses on file` : ''}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {instOpen && addressStep && (
                        <div style={S.suggestBox}>
                            <div style={{ ...S.suggestItem, cursor: 'pointer', color: '#6E58A6', fontWeight: 700, fontSize: 12.5 }} onClick={() => setAddressStep(null)}>
                                ← Back
                            </div>
                            <div style={{ padding: '4px 14px', fontSize: 11.5, color: '#9A90AE' }}>Multiple addresses on file — pick the right one</div>
                            {addressStep.map(row => (
                                <div key={row.id} style={S.suggestItem} onClick={() => pickInstitution(row)}>
                                    <div style={{ fontSize: 13, whiteSpace: 'pre-line' }}>{row.buyer_address || 'No address set'}</div>
                                    {row.gstin && <div style={{ fontSize: 11.5, color: '#8C82A0', marginTop: 2 }}>GSTIN {row.gstin}</div>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {selectedInst && (
                <>
                    <div style={S.section}>
                        <div style={S.fieldLabel}>Contact Person</div>
                        {!showNewContact ? (
                            <>
                                <select style={S.input} value={contactId} onChange={e => setContactId(e.target.value)}>
                                    <option value="">Select a contact...</option>
                                    {(selectedInst.contacts || []).map(c => (
                                        <option key={c.id} value={c.id}>{c.contact_person}{c.mobile ? ` — ${c.mobile}` : ''}</option>
                                    ))}
                                </select>
                                <button type="button" style={S.linkBtn} onClick={() => { setShowNewContact(true); setContactId(''); }}>
                                    + Add a new contact person
                                </button>
                            </>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <input style={S.input} placeholder="Contact person name" value={newContactName} onChange={e => setNewContactName(e.target.value)} />
                                <input style={S.input} placeholder="Mobile number" value={newContactMobile} onChange={e => setNewContactMobile(e.target.value)} />
                                <button type="button" style={S.linkBtn} onClick={() => { setShowNewContact(false); setNewContactName(''); setNewContactMobile(''); }}>
                                    Use an existing contact instead
                                </button>
                            </div>
                        )}
                    </div>

                    {(selectedInst.buyer_address || selectedInst.consignee_address || selectedInst.gstin) && (
                        <div style={S.addressBox}>
                            {selectedInst.gstin && (
                                <div style={{ marginBottom: 8 }}>
                                    <div style={S.smallLabel}>GSTIN</div>
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{selectedInst.gstin}</div>
                                </div>
                            )}
                            <div style={S.twoCol}>
                                <div>
                                    <div style={S.smallLabel}>Buyer Address</div>
                                    <div style={{ fontSize: 13, whiteSpace: 'pre-line' }}>{selectedInst.buyer_address || '—'}</div>
                                </div>
                                <div>
                                    <div style={S.smallLabel}>Consignee Address</div>
                                    <div style={{ fontSize: 13, whiteSpace: 'pre-line' }}>{selectedInst.consignee_address || '—'}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div style={S.twoCol}>
                        <div style={S.section}>
                            <div style={S.fieldLabel}>PO Date</div>
                            <input type="date" style={S.input} value={poDate} onChange={e => setPoDate(e.target.value)} />
                        </div>
                        <div style={S.section}>
                            <div style={S.fieldLabel}>Location</div>
                            <input style={S.input} value={location} onChange={e => setLocation(e.target.value)} placeholder="Delivery location" />
                        </div>
                    </div>

                    <div style={S.section}>
                        <div style={S.fieldLabel}>PO Number</div>
                        <input
                            style={S.input}
                            value={poNumber}
                            disabled={autoPoNumber}
                            placeholder={autoPoNumber ? 'Auto-generating...' : 'Enter PO number'}
                            onChange={e => setPoNumber(e.target.value)}
                        />
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 12.5 }}>
                            <input type="checkbox" checked={autoPoNumber} onChange={e => setAutoPoNumber(e.target.checked)} />
                            Auto generate PO no.
                        </label>
                    </div>

                    <div style={S.section}>
                        <div style={S.fieldLabel}>Remark</div>
                        <textarea
                            style={{ ...S.input, resize: 'vertical', fontFamily: 'inherit' }}
                            rows={2}
                            value={remark}
                            onChange={e => setRemark(e.target.value)}
                            placeholder="Optional note for Accounts (visible when this order is picked up for invoicing)"
                        />
                    </div>

                    <div style={S.section}>
                        <div style={S.fieldLabel}>Add Products</div>
                        <div style={{ position: 'relative' }}>
                            <input
                                style={S.input}
                                placeholder="Search product name or EAN"
                                value={skuSearch}
                                onFocus={() => setSkuOpen(true)}
                                onChange={e => { setSkuSearch(e.target.value); setSkuOpen(true); }}
                            />
                            {skuOpen && skuResults.length > 0 && (
                                <div style={S.suggestBox}>
                                    {skuResults.slice(0, 20).map(s => (
                                        <div key={s.ean_number} style={S.suggestItem} onClick={() => addLine(s)}>
                                            <div style={{ fontWeight: 700 }}>{s.product_name}</div>
                                            <div style={{ fontSize: 12, color: '#8C82A0' }}>EAN {s.ean_number} · MRP {fmtINR(s.mrp)}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {lines.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                                {lines.map(l => {
                                    const lineBase = Number(l.qty || 0) * Number(l.mrp || 0);
                                    const lineGst = lineBase * Number(l.gst_percent || 0) / 100;
                                    const lineTotal = lineBase + lineGst;
                                    return (
                                    <div key={l.ean} style={S.lineCard}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                            <div style={{ minWidth: 0, flex: 1 }}>
                                                <div style={{ fontWeight: 700, fontSize: 13.5, wordBreak: 'break-word' }}>{l.product_name}</div>
                                                <div style={{ fontSize: 11.5, color: '#8C82A0' }}>EAN {l.ean}</div>
                                            </div>
                                            <button type="button" style={S.removeBtn} onClick={() => removeLine(l.ean)}>✕</button>
                                        </div>
                                        <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <span style={S.qtyMiniLabel}>MRP</span>
                                                <input
                                                    type="number" min="0" step="0.01" style={S.mrpInput}
                                                    value={l.mrp}
                                                    onChange={e => updateMrp(l.ean, e.target.value)}
                                                />
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <span style={S.qtyMiniLabel}>Qty</span>
                                                <input
                                                    type="number" min="1" style={S.qtyInput}
                                                    value={l.qty}
                                                    onChange={e => updateQty(l.ean, e.target.value)}
                                                />
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <span style={S.qtyMiniLabel}>GST%</span>
                                                <input
                                                    type="number" min="0" step="0.01" style={S.mrpInput}
                                                    value={l.gst_percent ?? ''}
                                                    onChange={e => updateGst(l.ean, e.target.value)}
                                                />
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end', justifyContent: 'flex-end', marginLeft: 'auto' }}>
                                                <span style={{ fontSize: 11, color: '#8C82A0' }}>{fmtINR(lineBase)} + {fmtINR(lineGst)} GST</span>
                                                <span style={{ fontSize: 13, fontWeight: 700 }}>{fmtINR(lineTotal)}</span>
                                            </div>
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {lines.length > 0 && (
                        <div style={S.totalsBar}>
                            <span>{fmtNum(totalUnits)} pcs</span>
                            <span style={{ fontSize: 11.5, color: '#8C82A0' }}>{fmtINR(totalBase)} + {fmtINR(totalGst)} GST</span>
                            <span style={{ fontWeight: 700 }}>{fmtINR(totalAmount)}</span>
                        </div>
                    )}

                    <button type="button" style={S.submitBtn} onClick={openReview}>
                        Review Order
                    </button>
                </>
            )}

            {showReview && selectedInst && (
                <div style={S.overlay} onClick={() => !submitting && setShowReview(false)}>
                    <div style={S.sheet} onClick={e => e.stopPropagation()}>
                        <div style={S.sheetHeader}>
                            <h3 style={{ margin: 0, fontSize: 17 }}>Review Order</h3>
                            <button style={S.closeBtn} onClick={() => setShowReview(false)}>✕</button>
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <div style={S.fieldGrid}>
                                <div>
                                    <div style={S.smallLabel}>Institution</div>
                                    <div style={{ fontSize: 13.5, fontWeight: 600 }}>{selectedInst.institution_name}</div>
                                </div>
                                <div>
                                    <div style={S.smallLabel}>Contact</div>
                                    <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                                        {showNewContact ? `${newContactName} (new)` : (selectedContact ? `${selectedContact.contact_person}${selectedContact.mobile ? ' — ' + selectedContact.mobile : ''}` : '—')}
                                    </div>
                                </div>
                                <div>
                                    <div style={S.smallLabel}>PO Date</div>
                                    <div style={{ fontSize: 13.5, fontWeight: 600 }}>{formatDate(poDate)}</div>
                                </div>
                                <div>
                                    <div style={S.smallLabel}>Location</div>
                                    <div style={{ fontSize: 13.5, fontWeight: 600 }}>{location || '—'}</div>
                                </div>
                            </div>

                            {(selectedInst.buyer_address || selectedInst.consignee_address) && (
                                <div style={S.addressBox}>
                                    <div style={S.twoCol}>
                                        <div>
                                            <div style={S.smallLabel}>Buyer Address</div>
                                            <div style={{ fontSize: 13, whiteSpace: 'pre-line' }}>{selectedInst.buyer_address || '—'}</div>
                                        </div>
                                        <div>
                                            <div style={S.smallLabel}>Consignee Address</div>
                                            <div style={{ fontSize: 13, whiteSpace: 'pre-line' }}>{selectedInst.consignee_address || '—'}</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {remark.trim() && (
                                <div style={S.addressBox}>
                                    <div style={S.smallLabel}>Remark</div>
                                    <div style={{ fontSize: 13, whiteSpace: 'pre-line' }}>{remark}</div>
                                </div>
                            )}

                            <div>
                                <h4 style={S.sectionTitle}>Line Items</h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {lines.map(l => (
                                        <div key={l.ean} style={S.lineCard}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                                <div style={{ minWidth: 0 }}>
                                                    <div style={{ fontWeight: 700, fontSize: 13.5, wordBreak: 'break-word' }}>{l.product_name}</div>
                                                    <div style={{ fontSize: 11.5, color: '#8C82A0' }}>EAN {l.ean} · MRP {fmtINR(l.mrp)} × {fmtNum(l.qty)}</div>
                                                </div>
                                                <div style={{ fontWeight: 700, fontSize: 13.5, flexShrink: 0 }}>{fmtINR(Number(l.qty || 0) * Number(l.mrp || 0))}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div style={S.totalsBar}>
                                <span>{fmtNum(totalUnits)} pcs</span>
                                <span style={{ fontWeight: 700 }}>{fmtINR(totalAmount)}</span>
                            </div>
                        </div>

                        <div style={S.actionBar}>
                            <button style={S.actionBtnGhost} disabled={submitting} onClick={() => setShowReview(false)}>Back to Edit</button>
                            <button style={S.actionBtnPrimary} disabled={submitting} onClick={handleSubmit}>
                                {submitting ? 'Submitting...' : 'Confirm & Submit'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ================================= History ================================= */

function HistoryTab({ showToast }) {
    const [search, setSearch] = useState('');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [viewTarget, setViewTarget] = useState(null);
    const [viewData, setViewData] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);
    const [classifying, setClassifying] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const load = useCallback(async (q) => {
        setLoading(true);
        try {
            const res = await fetch(`${BASE}/institutional-orders?action=list&search=${encodeURIComponent(q || '')}`);
            const data = await res.json();
            setRows(Array.isArray(data) ? data : []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(''); }, [load]);
    useEffect(() => { const t = setTimeout(() => load(search), 300); return () => clearTimeout(t); }, [search, load]);

    async function openView(poNumber) {
        setViewTarget(poNumber); setViewData(null); setViewLoading(true);
        try {
            const res = await fetch(`${BASE}/institutional-orders?action=detail&po_number=${encodeURIComponent(poNumber)}`);
            const data = await res.json();
            if (res.ok) setViewData(data);
        } catch (e) { console.error(e); }
        finally { setViewLoading(false); }
    }

    async function classify(orderType) {
        if (!viewTarget) return;
        setClassifying(true);
        try {
            const res = await fetch(`${BASE}/institutional-orders?action=set-order-type`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ po_number: viewTarget, order_type: orderType }),
            });
            const data = await res.json();
            if (!res.ok) { showToast(data.error || 'Could not update'); return; }
            showToast(`Marked as ${orderType}`);
            setViewTarget(null); setViewData(null);
            load(search);
        } catch (e) { console.error(e); showToast('Something went wrong'); }
        finally { setClassifying(false); }
    }

    async function handleDelete() {
        if (!viewTarget) return;
        if (!window.confirm(`Permanently delete ${viewTarget}? This cannot be undone.`)) return;
        setDeleting(true);
        try {
            const res = await fetch(`${BASE}/institutional-orders?action=delete`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ po_number: viewTarget }),
            });
            const data = await res.json();
            if (!res.ok) { showToast(data.error || 'Could not delete'); return; }
            showToast(`${viewTarget} deleted`);
            setViewTarget(null); setViewData(null);
            load(search);
        } catch (e) { console.error(e); showToast('Something went wrong'); }
        finally { setDeleting(false); }
    }

    return (
        <div>
            <input style={{ ...S.input, marginBottom: 14 }} placeholder="Search PO number or institution..." value={search} onChange={e => setSearch(e.target.value)} />
            {loading ? (
                <p style={S.empty}>Loading...</p>
            ) : rows.length === 0 ? (
                <p style={S.empty}>No institutional orders yet.</p>
            ) : (
                <div style={S.cardList}>
                    {rows.map(r => (
                        <button key={r.po_number} className="io-card" style={S.card} onClick={() => openView(r.po_number)}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, fontSize: 14 }}>{r.po_number}</div>
                                    <div style={{ fontSize: 13, color: '#5B5468', wordBreak: 'break-word' }}>{r.party_name}</div>
                                </div>
                                <span style={{ ...S.chip, background: r.order_type ? '#E7F0E8' : '#F5EDDC', color: r.order_type ? '#5C7E60' : '#9A7B3E', flexShrink: 0 }}>
                                    {r.order_type || 'Pending'}
                                </span>
                            </div>
                            <div style={S.cardMetaRow}>
                                <span>{formatDate(r.po_date)}</span>
                                <span style={{ fontWeight: 700 }}>{fmtNum(r.po_units)} pcs · {fmtINR(r.po_amount)}</span>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {viewTarget && (
                <div style={S.overlay} onClick={() => setViewTarget(null)}>
                    <div style={S.sheet} onClick={e => e.stopPropagation()}>
                        <div style={S.sheetHeader}>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <h3 style={{ margin: 0, fontSize: 17 }}>PO {viewTarget}</h3>
                                    {viewData?.header?.remark && (
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 5,
                                            background: '#C62828', color: '#fff', border: '1px solid #C62828',
                                            borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700,
                                        }}>
                                            ⚠️ {viewData.header.remark}
                                        </span>
                                    )}
                                </div>
                                {viewData?.header?.party_name && <p style={{ margin: '4px 0 0', fontSize: 13, color: '#5B5468' }}>{viewData.header.party_name}</p>}
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                {viewData && (
                                    <button style={S.pdfBtn} onClick={() => downloadPdf(viewTarget, showToast)}>Download PDF</button>
                                )}
                                <button style={S.closeBtn} onClick={() => setViewTarget(null)}>✕</button>
                            </div>
                        </div>

                        {!viewData ? (
                            <p style={{ ...S.empty, padding: '0 20px 20px' }}>{viewLoading ? 'Loading...' : 'No details found.'}</p>
                        ) : (
                            <>
                                <div style={S.fieldGrid}>
                                    {[
                                        { label: 'Order Type', value: viewData.header.order_type || 'Pending' },
                                        { label: 'PO Date', value: formatDate(viewData.header.po_date) },
                                        { label: 'Location', value: viewData.header.location || '—' },
                                        { label: 'Contact', value: viewData.header.contact_person || '—' },
                                        { label: 'Mobile', value: viewData.header.mobile || '—' },
                                        { label: 'Sub Category', value: viewData.header.sub_category || '—' },
                                        { label: 'GSTIN', value: viewData.header.gstin || '—' },
                                        { label: 'Total', value: `${fmtNum(viewData.header.po_units)} pcs · ${fmtINR(viewData.header.po_amount)}` },
                                    ].map((item, i) => (
                                        <div key={i}>
                                            <div style={S.smallLabel}>{item.label}</div>
                                            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{item.value}</div>
                                        </div>
                                    ))}
                                    {(viewData.header.buyer_address || viewData.header.consignee_address) && (
                                        <>
                                            <div style={{ gridColumn: '1 / -1' }}>
                                                <div style={S.smallLabel}>Buyer Address</div>
                                                <div style={{ fontSize: 13, whiteSpace: 'pre-line' }}>{viewData.header.buyer_address || '—'}</div>
                                            </div>
                                            <div style={{ gridColumn: '1 / -1' }}>
                                                <div style={S.smallLabel}>Consignee Address</div>
                                                <div style={{ fontSize: 13, whiteSpace: 'pre-line' }}>{viewData.header.consignee_address || '—'}</div>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '16px 20px' }}>
                                    <h4 style={S.sectionTitle}>Line Items</h4>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {viewData.lines.map(li => (
                                            <div key={li.id} style={S.lineCard}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                                    <div style={{ minWidth: 0 }}>
                                                        <div style={{ fontWeight: 700, fontSize: 13.5, wordBreak: 'break-word' }}>{li.product_name}</div>
                                                        <div style={{ fontSize: 11.5, color: '#8C82A0' }}>EAN {li.ean_code}</div>
                                                    </div>
                                                    <div style={{ fontWeight: 700, fontSize: 13.5, flexShrink: 0 }}>{fmtINR(li.po_amount)}</div>
                                                </div>
                                                <div style={S.statChipRow}>
                                                    <span style={S.statChip}>MRP {fmtINR(li.mrp)}</span>
                                                    <span style={S.statChip}>GST {li.gst_percent != null ? `${li.gst_percent}%` : '—'}</span>
                                                    <span style={S.statChip}>HSN {li.hsn_number || '—'}</span>
                                                    <span style={S.statChip}>Qty {li.po_qty}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {!viewData.header.order_type && (
                                    <>
                                        <div style={S.actionBar}>
                                            <button style={S.actionBtnGhost} disabled={classifying || deleting} onClick={() => classify('Proforma Invoice')}>Mark as Proforma Invoice</button>
                                            <button style={S.actionBtnPrimary} disabled={classifying || deleting} onClick={() => classify('Sales Order')}>Mark as Sales Order</button>
                                        </div>
                                        <div style={{ ...S.actionBar, borderTop: 'none', paddingTop: 0 }}>
                                            <button style={S.actionBtnDanger} disabled={classifying || deleting} onClick={handleDelete}>
                                                {deleting ? 'Deleting...' : 'Delete Order'}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/* =========================== Manage Institutions =========================== */

function ManageInstitutionsTab({ institutions, loading, reload, showToast }) {
    const [search, setSearch] = useState('');
    const [editing, setEditing] = useState(null); // institution object or {} for new
    const [saving, setSaving] = useState(false);
    const [contactDraft, setContactDraft] = useState({ contact_person: '', mobile: '' });
    const [addingContactTo, setAddingContactTo] = useState(null);

    const filtered = institutions.filter(i => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return i.institution_name.toLowerCase().includes(q) || (i.location || '').toLowerCase().includes(q);
    });

    function startNew() { setEditing({ institution_name: '', sub_category: '', location: '', buyer_address: '', consignee_address: '', gstin: '' }); }
    function startEdit(inst) { setEditing({ ...inst }); }

    async function save() {
        if (!editing.institution_name?.trim()) { showToast('Institution name is required'); return; }
        setSaving(true);
        try {
            const isNew = !editing.id;
            const res = await fetch(`${BASE}/institution-master?action=${isNew ? 'create' : 'update'}`, {
                method: isNew ? 'POST' : 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editing),
            });
            const data = await res.json();
            if (!res.ok) { showToast(data.error || 'Could not save'); setSaving(false); return; }
            showToast(isNew ? 'Institution added' : 'Institution updated');
            setEditing(null);
            reload();
        } catch (e) { console.error(e); showToast('Something went wrong'); }
        finally { setSaving(false); }
    }

    async function addContact(institutionId) {
        if (!contactDraft.contact_person.trim()) { showToast('Contact name is required'); return; }
        try {
            const res = await fetch(`${BASE}/institution-master?action=create-contact`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ institution_id: institutionId, contact_person: contactDraft.contact_person.trim(), mobile: contactDraft.mobile.trim() || null }),
            });
            const data = await res.json();
            if (!res.ok) { showToast(data.error || 'Could not add contact'); return; }
            showToast('Contact added');
            setContactDraft({ contact_person: '', mobile: '' });
            setAddingContactTo(null);
            reload();
        } catch (e) { console.error(e); showToast('Something went wrong'); }
    }

    return (
        <div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                <input style={{ ...S.input, flex: 1 }} placeholder="Search institutions..." value={search} onChange={e => setSearch(e.target.value)} />
                <button type="button" style={S.addBtn} onClick={startNew}>+ Add</button>
            </div>

            {loading ? (
                <p style={S.empty}>Loading...</p>
            ) : filtered.length === 0 ? (
                <p style={S.empty}>No institutions yet — add one above.</p>
            ) : (
                <div style={S.cardList}>
                    {filtered.map(i => (
                        <div key={i.id} style={S.instCard}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, fontSize: 14 }}>{i.institution_name}</div>
                                    <div style={{ fontSize: 12.5, color: '#8C82A0' }}>{i.location || 'No location'} {i.sub_category ? `· ${i.sub_category}` : ''}</div>
                                </div>
                                <button type="button" style={S.editBtn} onClick={() => startEdit(i)}>Edit</button>
                            </div>
                            <div style={{ marginTop: 10 }}>
                                <div style={S.smallLabel}>Contacts</div>
                                {(i.contacts || []).length === 0 && addingContactTo !== i.id && (
                                    <div style={{ fontSize: 12.5, color: '#9A90AE' }}>No contacts yet.</div>
                                )}
                                {(i.contacts || []).map(c => (
                                    <div key={c.id} style={{ fontSize: 13, padding: '4px 0' }}>{c.contact_person}{c.mobile ? ` — ${c.mobile}` : ''}</div>
                                ))}
                                {addingContactTo === i.id ? (
                                    <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                                        <input style={{ ...S.input, flex: '1 1 140px' }} placeholder="Name" value={contactDraft.contact_person} onChange={e => setContactDraft(d => ({ ...d, contact_person: e.target.value }))} />
                                        <input style={{ ...S.input, flex: '1 1 120px' }} placeholder="Mobile" value={contactDraft.mobile} onChange={e => setContactDraft(d => ({ ...d, mobile: e.target.value }))} />
                                        <button type="button" style={S.smallSaveBtn} onClick={() => addContact(i.id)}>Save</button>
                                        <button type="button" style={S.linkBtn} onClick={() => setAddingContactTo(null)}>Cancel</button>
                                    </div>
                                ) : (
                                    <button type="button" style={S.linkBtn} onClick={() => { setAddingContactTo(i.id); setContactDraft({ contact_person: '', mobile: '' }); }}>+ Add contact</button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <div style={S.overlay} onClick={() => !saving && setEditing(null)}>
                    <div style={S.sheet} onClick={e => e.stopPropagation()}>
                        <div style={S.sheetHeader}>
                            <h3 style={{ margin: 0, fontSize: 17 }}>{editing.id ? 'Edit Institution' : 'Add Institution'}</h3>
                            <button style={S.closeBtn} onClick={() => setEditing(null)}>✕</button>
                        </div>
                        <div style={{ padding: 20, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {[
                                { key: 'institution_name', label: 'Institution Name' },
                                { key: 'sub_category', label: 'Sub Category' },
                                { key: 'location', label: 'Location' },
                                { key: 'gstin', label: 'GSTIN' },
                            ].map(f => (
                                <div key={f.key}>
                                    <div style={S.fieldLabel}>{f.label}</div>
                                    <input style={S.input} value={editing[f.key] || ''} onChange={e => setEditing(ed => ({ ...ed, [f.key]: e.target.value }))} />
                                </div>
                            ))}
                            <div>
                                <div style={S.fieldLabel}>Buyer Address</div>
                                <textarea style={{ ...S.input, height: 70, resize: 'vertical' }} value={editing.buyer_address || ''} onChange={e => setEditing(ed => ({ ...ed, buyer_address: e.target.value }))} />
                            </div>
                            <div>
                                <div style={S.fieldLabel}>Consignee Address</div>
                                <textarea style={{ ...S.input, height: 70, resize: 'vertical' }} value={editing.consignee_address || ''} onChange={e => setEditing(ed => ({ ...ed, consignee_address: e.target.value }))} />
                            </div>
                            <button type="button" style={S.submitBtn} disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const S = {
    page: { maxWidth: 900, margin: '0 auto', padding: '16px 8px 60px', fontFamily: "'Poppins', 'Segoe UI', sans-serif", color: '#231C30' },
    headerBar: { position: 'relative', display: 'flex', alignItems: 'center', gap: 14, background: '#0B0A12', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)', padding: '18px 20px', marginBottom: 20, boxShadow: '0 6px 20px rgba(35,28,48,0.15)', overflow: 'hidden' },
    h1: { fontSize: 20, fontWeight: 700, margin: 0, color: '#F3EFFA' },
    backBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 9, border: '1px solid rgba(255,255,255,0.16)', background: 'rgba(255,255,255,0.08)', color: '#F3EFFA', fontWeight: 600, fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 },
    greeting: { fontSize: 17, fontWeight: 700, color: '#231C30', margin: '0 0 14px' },
    startGrid: { display: 'flex', flexDirection: 'column', gap: 12 },
    startCard: { display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left', width: '100%', border: 'none', borderRadius: 16, background: '#6E58A6', padding: '18px 20px', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 14px rgba(110,88,166,0.28)' },
    startIcon: { fontSize: 26, flexShrink: 0, width: 48, height: 48, borderRadius: 14, background: 'rgba(255,255,255,0.16)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    startTitle: { display: 'block', fontWeight: 700, fontSize: 15.5, marginBottom: 3, color: '#fff' },
    startSub: { display: 'block', fontSize: 12.5, color: 'rgba(255,255,255,0.78)', fontWeight: 400 },
    formWrap: { display: 'flex', flexDirection: 'column', gap: 16 },
    section: {},
    twoCol: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 },
    fieldLabel: { fontSize: 11.5, fontWeight: 700, color: '#5B5468', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 6 },
    input: { width: '100%', boxSizing: 'border-box', padding: '11px 14px', borderRadius: 10, border: '1px solid #E4DEEE', fontSize: 13.5, outline: 'none', fontFamily: 'inherit', background: '#fff' },
    suggestBox: { position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff', border: '1px solid #ECE7F2', borderRadius: 10, boxShadow: '0 10px 30px rgba(35,28,48,0.12)', maxHeight: 260, overflowY: 'auto' },
    suggestItem: { padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #F4F1F8' },
    suggestEmpty: { padding: '12px 14px', fontSize: 12.5, color: '#9A90AE' },
    linkBtn: { border: 'none', background: 'transparent', color: '#6E58A6', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', padding: '6px 0', fontFamily: 'inherit' },
    addressBox: { background: '#F4F1F8', borderRadius: 12, padding: '12px 14px' },
    lineCard: { border: '1px solid #ECE7F2', borderRadius: 12, padding: '10px 12px' },
    statChipRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
    statChip: { fontSize: 11, fontWeight: 600, color: '#5B5468', background: '#F4F1F8', borderRadius: 999, padding: '4px 9px', whiteSpace: 'nowrap' },
    qtyMiniLabel: { fontSize: 9.5, fontWeight: 700, color: '#8C82A0', textTransform: 'uppercase', letterSpacing: '0.03em' },
    qtyInput: { width: 56, padding: '8px 6px', borderRadius: 8, border: '1px solid #E4DEEE', fontSize: 13, textAlign: 'center', fontFamily: 'inherit' },
    mrpInput: { width: 76, padding: '8px 6px', borderRadius: 8, border: '1px solid #E4DEEE', fontSize: 13, textAlign: 'center', fontFamily: 'inherit' },
    removeBtn: { border: 'none', background: '#F7E5EA', color: '#B05770', width: 28, height: 28, borderRadius: 8, cursor: 'pointer', flexShrink: 0 },
    totalsBar: { display: 'flex', justifyContent: 'space-between', padding: '12px 14px', background: '#F4F1F8', borderRadius: 10, fontSize: 13.5 },
    submitBtn: { padding: '14px', borderRadius: 12, border: 'none', background: '#6E58A6', color: '#fff', fontWeight: 700, fontSize: 14.5, cursor: 'pointer', fontFamily: 'inherit' },
    empty: { padding: '40px 0', textAlign: 'center', color: '#9A90AE', fontSize: 13.5 },
    cardList: { display: 'flex', flexDirection: 'column', gap: 10 },
    card: { textAlign: 'left', width: '100%', border: '1px solid #ECE7F2', borderRadius: 14, background: '#fff', padding: '14px 16px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', gap: 8 },
    instCard: { border: '1px solid #ECE7F2', borderRadius: 14, background: '#fff', padding: '14px 16px' },
    cardMetaRow: { display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: '#5B5468' },
    chip: { display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700 },
    smallLabel: { fontSize: 10, fontWeight: 700, color: '#8C82A0', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 },
    addBtn: { padding: '10px 16px', borderRadius: 10, border: 'none', background: '#6E58A6', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 },
    editBtn: { padding: '6px 12px', borderRadius: 8, border: '1px solid #E4DEEE', background: '#fff', color: '#6E58A6', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0, height: 'fit-content' },
    smallSaveBtn: { padding: '8px 12px', borderRadius: 8, border: 'none', background: '#5C7E60', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(35,28,48,0.45)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
    sheet: { width: '100%', maxWidth: 720, maxHeight: '92vh', background: '#fff', borderRadius: '18px 18px 0 0', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    sheetHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '18px 20px', flex: 'none', borderBottom: '1px solid #ECE7F2' },
    closeBtn: { border: 'none', background: '#F1EEF6', color: '#6B6379', width: 32, height: 32, borderRadius: 10, fontSize: 14, cursor: 'pointer', flexShrink: 0 },
    pdfBtn: { border: '1px solid #E4DEEE', background: '#fff', color: '#6E58A6', fontWeight: 700, fontSize: 12, padding: '0 12px', height: 32, borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0, whiteSpace: 'nowrap' },
    fieldGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '14px 16px', padding: '16px 20px', borderBottom: '1px solid #ECE7F2', flex: 'none' },
    sectionTitle: { fontSize: 12.5, fontWeight: 700, margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: '0.03em' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 480 },
    th: { textAlign: 'left', padding: '8px 10px', background: '#F4F1F8', fontSize: 10.5, fontWeight: 700, color: '#5B5468', whiteSpace: 'nowrap' },
    td: { padding: '8px 10px', borderBottom: '1px solid #F1EEF6', whiteSpace: 'nowrap' },
    actionBar: { display: 'flex', gap: 10, padding: '14px 20px', borderTop: '1px solid #ECE7F2', flex: 'none' },
    actionBtnGhost: { flex: 1, padding: '12px', borderRadius: 10, border: '1px solid #E4DEEE', background: '#fff', color: '#231C30', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
    actionBtnPrimary: { flex: 1, padding: '12px', borderRadius: 10, border: 'none', background: '#5C7E60', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
    actionBtnDanger: { flex: 1, padding: '12px', borderRadius: 10, border: '1px solid #F7E5EA', background: '#fff', color: '#C25E77', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
    toast: { position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#231C30', color: '#fff', padding: '12px 20px', borderRadius: 10, fontSize: 13, fontWeight: 500, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1200, maxWidth: '90%', textAlign: 'center' },
};

const EMBEDDED_CSS = DPO_BEAM_CSS + `
.io-card:hover, .io-tab:hover { box-shadow: 0 4px 14px rgba(35,28,48,0.06); }
@media (max-width: 640px) {
    .io-page { margin-left: -22px; margin-right: -22px; padding-left: 10px !important; padding-right: 10px !important; }
}
`;
